import React, { useState, useEffect, useCallback } from "react";
import { useApiClient } from "../lib/api";
import Skeleton from "../components/common/Skeleton";
import SortableTh from "../components/common/SortableTh";
import Pagination from "../components/common/Pagination";
import { useNotification } from "../providers/notification-provider";
import { Dialog, Switch } from "@headlessui/react";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../providers/auth-provider";
import StatusPill from "../components/common/StatusPill";
import { maskCNPJ, maskPhone, maskCPF, unmask } from "../utils/maskUtils";
import { validateBrazilianDocument, validateEmail } from "../utils/validationUtils";
import { UI_EVENTS } from "../config";
import ContextHelpButton from "../components/help/ContextHelpButton";
import ContextHelpDrawer from "../components/help/ContextHelpDrawer";
import { COMPANIES_HELP_SECTIONS } from "../components/help/waiterCallHelpContent";

export default function CompaniesPage() {
  const { showToast, showConfirm } = useNotification();
  const api = useApiClient();

  const [data, setData] = useState({ items: [], totalItems: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  // View Modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingCompany, setViewingCompany] = useState(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpSectionKey, setHelpSectionKey] = useState(COMPANIES_HELP_SECTIONS[0].key);

  const [searchTerm, setSearchTerm] = useState("");
  const pageSize = 10;
  const [page, setPage] = useState(1);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });

  const [formData, setFormData] = useState({
    name: "",
    document: "",
    phone: "",
    address: "",
    responsibleName: "",
    email: "",
    responsibleCpf: "",
    isActive: true,
  });

  const { user } = useAuth();

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/v1/admin/companies", {
        params: {
          page,
          pageSize,
          search: searchTerm || undefined,
          sortBy: sortConfig.key,
          sortDesc: sortConfig.direction === "desc",
          includeInactive
        }
      });
      if (response.data?.items) {
        setData(response.data);
      } else if (Array.isArray(response.data)) {
        setData({ items: response.data, totalItems: response.data.length, totalPages: 1 });
      }
    } catch (error) {
      console.error("Error fetching companies", error);
      showToast("Erro ao carregar as empresas.", "error");
    } finally {
      setLoading(false);
    }
  }, [api, page, pageSize, searchTerm, sortConfig, includeInactive, showToast]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCompanies();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchCompanies]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const openViewCompany = (company) => {
    setViewingCompany(company);
    setIsViewModalOpen(true);
  };

  const handleOpenModal = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        name: company.name || "",
        document: (() => {
          const rawDocument = unmask(company.document || "");
          return rawDocument.length > 11 ? maskCNPJ(rawDocument) : maskCPF(rawDocument);
        })(),
        phone: maskPhone(company.phone || ""),
        address: company.address || "",
        responsibleName: company.responsibleName || "",
        email: company.email || "",
        responsibleCpf: maskCPF(company.responsibleCpf || ""),
        isActive: company.isActive ?? true,
      });
    } else {
      setEditingCompany(null);
      setFormData({
        name: "",
        document: "",
        phone: "",
        address: "",
        responsibleName: "",
        email: "",
        responsibleCpf: "",
        isActive: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCompany(null);
    setFormData({
      name: "",
      document: "",
      phone: "",
      address: "",
      responsibleName: "",
      email: "",
      responsibleCpf: "",
      isActive: true,
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    if (name === "document") {
      const rawValue = value.replace(/\D/g, "").slice(0, 14);
      if (rawValue.length <= 11) {
        newValue = maskCPF(rawValue);
      } else {
        newValue = maskCNPJ(rawValue);
      }
    } else if (name === "phone") {
      newValue = maskPhone(value);
    } else if (name === "responsibleCpf") {
      newValue = maskCPF(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const willInactivate = editingCompany?.isActive && formData.isActive === false;
    if (willInactivate) {
      const confirmed = await showConfirm({
        title: 'Inativar empresa',
        message: `Deseja inativar a empresa ${editingCompany.name}?`,
        confirmText: 'Inativar',
        cancelText: 'Cancelar',
        type: 'warning'
      });

      if (!confirmed) {
        return;
      }
    }

    if (formData.email && !validateEmail(formData.email)) {
      showToast("Por favor, insira um endereço de email válido.", "warning");
      return;
    }

    const rawDoc = unmask(formData.document);
    if (!rawDoc) {
      showToast("Documento (CPF/CNPJ) é obrigatório.", "warning");
      return;
    }

    if (rawDoc.length !== 11 && rawDoc.length !== 14) {
      showToast("Documento inválido. Informe CPF (11 dígitos) ou CNPJ (14 dígitos).", "warning");
      return;
    }

    const documentValidation = validateBrazilianDocument(rawDoc);
    if (!documentValidation.isValid && documentValidation.type === null) {
      showToast("Documento inválido.", "warning");
      return;
    }

    const rawCPF = unmask(formData.responsibleCpf);
    if (rawCPF && rawCPF.length !== 11) {
      showToast("CPF do responsável inválido.", "warning");
      return;
    }

    const submissionData = {
      ...formData,
      document: rawDoc,
      phone: unmask(formData.phone),
      responsibleCpf: rawCPF
    };

    try {
      if (editingCompany) {
        await api.put(`/api/v1/admin/companies/${editingCompany.id}`, submissionData);
        showToast("Empresa atualizada com sucesso.", "success");
      } else {
        await api.post("/api/v1/admin/companies", submissionData);
        showToast("Empresa criada com sucesso.", "success");
      }
      handleCloseModal();
      fetchCompanies();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new window.CustomEvent(UI_EVENTS.scopeRefresh));
      }
    } catch (error) {
      console.error(error);
      const msg = error.response?.data || error.response?.data?.message || "Erro ao salvar empresa.";
      showToast(typeof msg === "string" ? msg : "Erro interno", "error");
    }
  };

  const archiveCompany = async (company) => {
     const confirmed = await showConfirm({
      title: "Remover Empresa",
      message: `Cuidado: isso removerá a empresa, a equipe e todos os seus restaurantes. Tem certeza absoluta que deseja excluir "${company.name}"?`,
      confirmText: "Excluir",
      type: "error"
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/v1/admin/companies/${company.id}`);
      showToast("Empresa removida com sucesso.", "success");
      fetchCompanies();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new window.CustomEvent(UI_EVENTS.scopeRefresh));
      }
    } catch (error) {
      console.error(error);
      showToast("Erro ao remover empresa.", "error");
    }
  };

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Empresas</h1>
          <p className="mt-2 text-sm text-gray-700">
            Lista de todas as empresas cadastradas no sistema.
          </p>
        </div>
        <div className="mt-4 flex flex-col gap-2 sm:ml-16 sm:mt-0 sm:flex-row sm:items-center sm:flex-none">
          <button
            type="button"
            onClick={() => handleOpenModal()}
            className="block w-full rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:w-auto"
          >
            <PlusIcon className="h-5 w-5 inline-block mr-1" />
            Nova Empresa
          </button>
          <ContextHelpButton onClick={() => setIsHelpOpen(true)} />
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row items-center gap-4">
        <input
          type="text"
          placeholder="Buscar por nome ou documento..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 max-w-md"
        />
        <div className="w-full sm:w-auto">
          <div className="flex h-[38px] items-center gap-3">
            <Switch
              checked={!includeInactive}
              onChange={(checked) => {
                setIncludeInactive(!checked);
                setPage(1);
              }}
              className={`${!includeInactive ? "bg-indigo-600" : "bg-gray-200"} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0`}
            >
              <span className="sr-only">Apenas ativas</span>
              <span
                aria-hidden="true"
                className={`${!includeInactive ? "translate-x-5" : "translate-x-0"} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
            <span className="text-sm text-gray-600">Apenas ativas</span>
          </div>
        </div>
      </div>

      <div className="mt-8 flow-root">
        <div className="sm:hidden space-y-3">
          {loading ? (
            <div className="bg-white p-4 space-y-3 rounded-lg shadow ring-1 ring-black ring-opacity-5">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
              Nenhuma empresa encontrada com os filtros atuais.
            </div>
          ) : (
            data.items.map((company) => (
              <article
                key={company.id}
                onClick={() => openViewCompany(company)}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{company.name}</h3>
                    <p className="text-xs text-gray-600">
                      {company.document
                        ? (() => {
                            const raw = unmask(company.document);
                            return raw.length > 11 ? maskCNPJ(raw) : maskCPF(raw);
                          })()
                        : '-'}
                    </p>
                    <p className="text-xs text-gray-500">{company.responsibleName || '-'}</p>
                  </div>
                  <StatusPill active={company.isActive} labelActive="Ativa" labelInactive="Inativa" />
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenModal(company);
                    }}
                    title="Editar"
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Editar
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      archiveCompany(company);
                    }}
                    title="Excluir"
                    className="inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Excluir
                  </button>
                </div>
              </article>
            ))
          )}

          {!loading && data.totalItems > 0 && (
            <Pagination
              currentPage={page}
              totalPages={data.totalPages}
              onPageChange={setPage}
              totalItems={data.totalItems}
              pageSize={pageSize}
            />
          )}
        </div>

        <div className="hidden sm:block -mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {loading ? (
                <div className="bg-white p-4 space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : (
                <>
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <SortableTh
                          className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                          sortKey="name"
                          currentSort={sortConfig}
                          onSort={handleSort}
                        >
                          Nome / Razão Social
                        </SortableTh>
                        <SortableTh
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          sortKey="document"
                          currentSort={sortConfig}
                          onSort={handleSort}
                        >
                          Documento
                        </SortableTh>
                        <SortableTh
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          sortKey="responsibleName"
                          currentSort={sortConfig}
                          onSort={handleSort}
                        >
                          Responsável
                        </SortableTh>
                        <SortableTh
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          sortKey="status"
                          currentSort={sortConfig}
                          onSort={handleSort}
                        >
                          Status
                        </SortableTh>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span className="sr-only">Edit</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {data.items.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-sm text-gray-500">
                            Nenhuma empresa encontrada com os filtros atuais.
                          </td>
                        </tr>
                      ) : (
                        data.items.map((company) => (
                          <tr 
                            key={company.id} 
                            onClick={() => openViewCompany(company)} 
                            className="cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                {company.name}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {company.document ? (() => {
                                 const raw = unmask(company.document);
                                 return raw.length > 11 ? maskCNPJ(raw) : maskCPF(raw);
                               })() : "-"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {company.responsibleName || "-"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                <StatusPill active={company.isActive} labelActive="Ativa" labelInactive="Inativa" />
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleOpenModal(company); }}
                                    title="Editar"
                                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                                >
                                    <PencilIcon className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); archiveCompany(company); }}
                                    title="Excluir"
                                    className="text-red-600 hover:text-red-900"
                                >
                                    <TrashIcon className="h-5 w-5" />
                                </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {!loading && data.totalItems > 0 && (
                    <Pagination
                        currentPage={page}
                        totalPages={data.totalPages}
                        onPageChange={setPage}
                        totalItems={data.totalItems}
                        pageSize={pageSize}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit/Create Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto p-4">
          <Dialog.Panel className="mx-auto max-h-[92dvh] w-full max-w-2xl overflow-y-auto rounded bg-white p-4 sm:p-6 shadow-xl">
            <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4 border-b pb-2">
              {editingCompany ? "Editar Empresa" : "Nova Empresa"}
            </Dialog.Title>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                    Nome da Empresa / Razão Social <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="document" className="block text-sm font-medium leading-6 text-gray-900">
                    Documento (CPF/CNPJ) <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="document"
                      name="document"
                      required
                      value={formData.document}
                      onChange={handleChange}
                      className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="phone" className="block text-sm font-medium leading-6 text-gray-900">
                    Telefone <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="address" className="block text-sm font-medium leading-6 text-gray-900">
                    Endereço <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="address"
                      name="address"
                      required
                      value={formData.address}
                      onChange={handleChange}
                      className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="responsibleName" className="block text-sm font-medium leading-6 text-gray-900">
                    Responsável <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="responsibleName"
                      name="responsibleName"
                      required
                      value={formData.responsibleName}
                      onChange={handleChange}
                      className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                    Email
                  </label>
                  <div className="mt-1">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="responsibleCpf" className="block text-sm font-medium leading-6 text-gray-900">
                    CPF Responsável
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="responsibleCpf"
                      name="responsibleCpf"
                      value={formData.responsibleCpf}
                      onChange={handleChange}
                      className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <div className="flex items-center gap-x-3 mt-2">
                    <input
                      id="isActive"
                      name="isActive"
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label htmlFor="isActive" className="text-sm font-medium leading-6 text-gray-900 !mb-0">
                      Empresa Ativa
                    </label>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-x-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="w-full rounded bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-900 sm:w-auto"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:w-auto"
                >
                  Salvar
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* View Modal */}
      <Dialog open={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto p-4">
          <Dialog.Panel className="relative mx-auto max-h-[92dvh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-4 shadow-2xl sm:p-6">
            <div className="mb-6 flex items-start justify-between">
                <div>
                    <Dialog.Title className="text-xl font-bold text-gray-900 sm:text-2xl">
                        {viewingCompany?.name}
                    </Dialog.Title>
                    <p className="text-sm text-gray-500 mt-1">Detalhes da Empresa</p>
                </div>
                <button onClick={() => setIsViewModalOpen(false)} className="text-gray-400 hover:text-gray-500">
                    <span className="sr-only">Fechar</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {viewingCompany && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Razão Social</h4>
                            <p className="text-base font-medium text-gray-900">{viewingCompany.name}</p>
                        </div>
                        <div>
                            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Documento</h4>
                            <p className="text-base font-medium text-gray-900">
                                {viewingCompany.document ? (() => {
                                    const raw = unmask(viewingCompany.document);
                                    return raw.length > 11 ? maskCNPJ(raw) : maskCPF(raw);
                                })() : "-"}
                            </p>
                        </div>
                        <div>
                            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Email</h4>
                            <p className="text-base font-medium text-gray-900">{viewingCompany.email || "-"}</p>
                        </div>
                        <div>
                            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Telefone</h4>
                            <p className="text-base font-medium text-gray-900">{maskPhone(viewingCompany.phone || "-")}</p>
                        </div>
                        <div>
                            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Data de Criação</h4>
                            <p className="text-base font-medium text-gray-900">
                                {new Date(viewingCompany.createdAt).toLocaleDateString("pt-BR", { year: "numeric", month: "2-digit", day: "2-digit" })}
                            </p>
                        </div>
                        <div>
                            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Status</h4>
                            <StatusPill active={viewingCompany.isActive} labelActive="Ativa" labelInactive="Inativa" />
                        </div>
                    </div>

                    <div className="border-t border-gray-100 pt-4">
                        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Endereço</h4>
                        <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700">
                            {viewingCompany.address || "-"}
                        </div>
                    </div>

                    <div className="border-t border-gray-100 pt-4">
                        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Responsável</h4>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                             <div className="flex justify-between items-center border-b border-gray-200 pb-2 mb-2">
                                <span className="text-sm font-medium text-gray-900">Nome:</span>
                                <span className="text-sm text-gray-800">{viewingCompany.responsibleName || "-"}</span>
                             </div>
                             <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-900">CPF:</span>
                                <span className="text-sm text-gray-800">{maskCPF(viewingCompany.responsibleCpf || "-")}</span>
                             </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-8 flex justify-end">
                 <button onClick={() => setIsViewModalOpen(false)} className="w-full rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-200 sm:w-auto">
                    Fechar
                 </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <ContextHelpDrawer
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title="Cadastro de Empresas"
        description="Revise os cuidados administrativos para criar, inativar e manter empresas com escopo operacional consistente."
        sections={COMPANIES_HELP_SECTIONS}
        activeSectionKey={helpSectionKey}
        onSectionChange={setHelpSectionKey}
      />
    </div>
  );
}
